import { useRef, useCallback, useState } from 'react'
import { FileText } from 'lucide-react'
import type { FacilityArea, SurveyDocument } from '../../lib/services/survey'
import { surveyStore, type PendingDocument } from '../../lib/offline/surveyStore'
import { enqueueDocument } from '../../lib/services/surveySync'

interface DocumentScannerProps {
  visitId: string
  surveyId: string
  area: FacilityArea | null
  onCapture?: (doc: PendingDocument) => void
}

const DOC_TYPES: SurveyDocument['doc_type'][] = ['policy', 'log', 'record', 'certificate', 'other']

export function DocumentScanner({ visitId, surveyId, area, onCapture }: DocumentScannerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [docType, setDocType] = useState<SurveyDocument['doc_type']>('policy')
  const [label, setLabel] = useState('')

  const handleFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const doc: PendingDocument = {
        document_id: crypto.randomUUID(),
        survey_id: surveyId,
        visit_id: visitId,
        doc_type: docType,
        label: label.trim() || file.name,
        blob: file,
        mime_type: file.type || 'application/pdf',
        area,
        created_at: new Date().toISOString(),
      }

      await surveyStore.saveDocument(doc)
      await enqueueDocument(visitId, surveyId, doc.document_id)
      onCapture?.(doc)

      setLabel('')
      if (inputRef.current) inputRef.current.value = ''
    },
    [visitId, surveyId, area, docType, label, onCapture],
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Document label…"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value as SurveyDocument['doc_type'])}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
        >
          {DOC_TYPES.map((t) => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
      </div>

      <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-[var(--brand-deep)] active:scale-[0.98]">
        <FileText size={16} />
        Scan / Upload
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={handleFile}
        />
      </label>
    </div>
  )
}
